'use client';

import clsx from 'clsx';
import { CSSProperties, FC, HTMLAttributes, ReactNode, useRef } from 'react';

import Image from '@/components/Image';
import Link from '@/components/Link';
import VimeoPlayer from '@/components/shared/VimeoPlayer';
import { useVimeoViewportPlayback } from '@/hooks/useVimeoViewportPlayback';

import css from './style.module.scss';

interface BaseCardProps extends Omit<HTMLAttributes<HTMLAnchorElement>, 'title'> {
	url: string;
	mainText?: ReactNode;
	subtitle?: ReactNode;
	videoUrl?: string | null;
	thumbnailUrl?: string | null;
	imageUrl?: string | null;
	imageAlt?: string | null;
	tags?: ReactNode;
	awards?: ReactNode;
	width?: string | number;
	className?: string;
	mediaClassName?: string;
	style?: CSSProperties;
	playing?: boolean;
	withOverlay?: boolean;
}

const BaseCard: FC<BaseCardProps> = ({
	url,
	mainText,
	subtitle,
	videoUrl,
	thumbnailUrl,
	imageUrl,
	imageAlt,
	tags,
	awards,
	width,
	className,
	mediaClassName,
	style,
	playing,
	withOverlay = true,
	onMouseEnter,
	onMouseLeave,
	...rest
}) => {
	const mediaWrapperRef = useRef<HTMLDivElement>(null);
	const vimeoInView = useVimeoViewportPlayback(mediaWrapperRef);
	const vimeoPlaying =
		videoUrl && (typeof playing === 'undefined' ? vimeoInView : vimeoInView && playing);

	const computedStyle: CSSProperties = {
		...(width ? { width: typeof width === 'number' ? `${width}%` : width } : {}),
		...style
	};

	return (
		<Link
			href={url || ''}
			className={clsx(css.card, className)}
			style={computedStyle}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			{...rest}
		>
			<div className={css.meta}>
				{tags}
				{awards}
			</div>
			<div ref={mediaWrapperRef} className={css.mediaWrapper}>
				{videoUrl ? (
					<VimeoPlayer
						fitCover
						url={videoUrl}
						thumbnailUrl={thumbnailUrl || undefined}
						thumbnailAlt={imageAlt || ''}
						className={clsx(css.media, mediaClassName)}
						playing={Boolean(vimeoPlaying)}
					/>
				) : (
					<Image
						className={clsx(css.media, mediaClassName)}
						width={800}
						height={600}
						src={imageUrl || ''}
						alt={imageAlt || ''}
					/>
				)}
			</div>
			<div className={clsx(css.overlay, { [css.withOverlay]: withOverlay })}>
				<div className={clsx(css.textWrapper, 'base-card__text-wrapper')}>
					{mainText ? <div className={css.mainText}>{mainText}</div> : null}
					{subtitle ? (
						<p className={clsx(css.subtitle, 'base-card__subtitle')}>{subtitle}</p>
					) : null}
				</div>
			</div>
		</Link>
	);
};

export default BaseCard;
